/**
 * Prepare a gasless sell operation
 */
import type { Address } from "viem"
import { Errors } from "../../errors"
import {
	deriveMarketStatus,
	type ReadableClient,
	readAllQuantities,
	readBucketCount,
	readEmergencyPausedAt,
	readMarketInfo,
	readPrices,
	readQuoteSell,
	readResolution,
	readUserPosition,
} from "../../internal/contracts"
import {
	applySellSlippageScale,
	createDeadline,
	createDeadlineDate,
	DEFAULT_DEADLINE_SECONDS,
	DEFAULT_SLIPPAGE_BPS,
	fromPriceScale,
	fromShareScale,
	fromUsdcScale,
	toShareScale,
	validateSlippageBps,
} from "../../internal/convert"
import { computePrice } from "../../internal/lmsr"
import { buildSellSigningPayload, generateNonce } from "../../internal/signing"
import type { SellPreparation, SellQuote } from "../../types"

export interface PrepareSellParams {
	/** Market contract address */
	market: Address
	/** User wallet address */
	user: Address
	/** Outcome bucket index (0-indexed) */
	bucket: number
	/** Amount of shares to sell (human-readable) */
	shares: number
	/** Chain ID (required - get from your app config) */
	chainId: number
	/** Slippage tolerance in basis points (default: 200 = 2%) */
	slippageBps?: number
	/** Deadline in seconds from now (default: 300 = 5 minutes) */
	deadlineSeconds?: number
}

/**
 * Prepare a gasless sell operation
 *
 * Uses the native sellWithSignature function on the market contract.
 * User signs once, backend executes.
 *
 * @example
 * ```ts
 * import { prepareSell } from "@breakout/sdk/actions/prepare"
 *
 * const preparation = await prepareSell(publicClient, {
 *   market: "0x...",
 *   user: "0x...",
 *   bucket: 0,
 *   shares: 50,
 * })
 * ```
 */
export async function prepareSell(
	client: ReadableClient,
	params: PrepareSellParams,
): Promise<SellPreparation> {
	const {
		market,
		user,
		bucket,
		shares,
		chainId,
		slippageBps = DEFAULT_SLIPPAGE_BPS,
		deadlineSeconds = DEFAULT_DEADLINE_SECONDS,
	} = params

	// Validate shares (check NaN, Infinity, negative)
	if (!Number.isFinite(shares) || shares <= 0) {
		throw Errors.invalidAmount("Share amount must be a positive number")
	}

	// Validate slippage
	validateSlippageBps(slippageBps)

	// Fetch market data in parallel
	const [info, resolution, pausedAt, bucketCount, position] = await Promise.all([
		readMarketInfo(client, market),
		readResolution(client, market),
		readEmergencyPausedAt(client, market),
		readBucketCount(client, market),
		readUserPosition(client, market, user),
	])

	// Validate bucket
	if (bucket < 0 || bucket >= bucketCount) {
		throw Errors.invalidBucket(bucket, bucketCount)
	}

	// Check market status
	const status = deriveMarketStatus(
		info.status,
		info.openTimeSec,
		info.cutoffTimeSec,
		resolution,
		pausedAt,
	)

	if (status.type === "paused") {
		throw Errors.marketPaused()
	}
	if (status.type === "pending") {
		throw Errors.marketPending(new Date(Date.now() + status.opensIn))
	}
	if (status.type !== "open") {
		throw Errors.marketClosed()
	}

	// Check user has enough shares
	const userShares = fromShareScale(position.shares[bucket] ?? 0n)
	if (userShares < shares) {
		throw Errors.insufficientShares(bucket, shares, userShares)
	}

	// Convert shares and get quote + data for accurate price impact
	const sharesScaled = toShareScale(shares)
	const [rawQuote, rawPrices, rawQuantities] = await Promise.all([
		readQuoteSell(client, market, bucket, sharesScaled),
		readPrices(client, market),
		readAllQuantities(client, market),
	])

	// Apply slippage to min proceeds
	const minProceedsWithSlippage = applySellSlippageScale(rawQuote.total, slippageBps)
	const deadline = createDeadline(deadlineSeconds)
	const expiresAt = createDeadlineDate(deadlineSeconds)

	// Generate nonce for the signature
	const nonce = generateNonce()

	// Build signing payload for native sellWithSignature
	const signingPayload = buildSellSigningPayload(chainId, market, {
		trader: user,
		bucket,
		shares: sharesScaled,
		minProceeds: minProceedsWithSlippage,
		nonce,
		deadline,
	})

	// Build the quote with accurate price impact from LMSR
	const priceBeforeRaw = rawPrices[bucket]
	if (priceBeforeRaw === undefined) {
		throw Errors.invalidBucket(bucket, rawPrices.length)
	}
	const priceBefore = fromPriceScale(priceBeforeRaw)
	const proceeds = fromUsdcScale(rawQuote.proceeds)
	const fee = fromUsdcScale(rawQuote.fee)
	const usdcOut = fromUsdcScale(rawQuote.total)
	const avgPrice = proceeds / shares

	// Calculate accurate priceAfter using LMSR
	const SHARE_SCALE = 1e6
	const quantities = rawQuantities.map(q => Number(q) / SHARE_SCALE)
	const liquidityParam = Number(info.liquidityParam)
	const quantitiesAfter = [...quantities]
	quantitiesAfter[bucket] = (quantitiesAfter[bucket] ?? 0) - shares
	const priceAfter = computePrice(quantitiesAfter, liquidityParam, bucket)
	const priceImpact = priceBefore > 0 ? (priceAfter - priceBefore) / priceBefore : 0

	const quote: SellQuote = {
		sharesIn: shares,
		usdcOut,
		proceeds,
		fee,
		avgPrice,
		priceImpact,
		priceBefore,
		priceAfter,
	}

	return {
		type: "sell",
		signingPayload,
		quote,
		expiresAt,
		_execution: {
			market,
			user,
			bucket,
			shares: sharesScaled,
			minProceeds: minProceedsWithSlippage,
			nonce,
			deadline,
		},
	}
}
